import { createFileRoute, Link } from "@tanstack/react-router";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { ArrowRight, Check } from "lucide-react";
import { primeServices } from "@/content/prime-services";
import { FitCheck } from "@/components/site/FitCheck";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { caseItems, featuredCase } from "@/content/cases";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";

const SITE_ORIGIN = "https://fenny.se";
const PRIME_URL = `${SITE_ORIGIN}/en/prime`;
import { SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import { aeoGeo } from "@/lib/seo/schema";
const PRIME_OG_IMAGE = SOCIAL_IMAGE_URL;

const primeShortTitlesEn: Record<string, string> = {
  reklamfilm: "Commercials",
  varumarkesfilm: "Brand film",
  foretagsfilm: "Corporate film",
  produktfilm: "Product film",
  rekryteringsfilm: "Recruitment film",
  utbildningsfilm: "Training film",
  evenemangsfilm: "Event film",
  dokumentar: "Documentary",
  musikvideo: "Music video",
  drönarfilm: "Aerial film",
  livesandning: "Live broadcast",
  podcast: "Podcast",
  studiopodd: "Studio podcast",
  animation: "Animation",
};

const primeIntrosEn: Record<string, string> = {
  reklamfilm: "Brand stories with cinematic craft — distinctive ideas, premium execution.",
  varumarkesfilm: "Films that define what your brand stands for, beyond a single campaign.",
  foretagsfilm: "Corporate films that explain who you are and why it matters.",
  produktfilm: "Product films that make the value obvious in seconds.",
  rekryteringsfilm: "Employer brand films that attract the right talent.",
  utbildningsfilm: "Training video that learners actually finish.",
  evenemangsfilm: "Event films that capture the energy and the message.",
  dokumentar: "Documentary storytelling with editorial integrity.",
  musikvideo: "Music videos with directorial ambition.",
  drönarfilm: "Aerial cinematography for scale and perspective.",
  livesandning: "Live broadcast with multi-cam, mixing and streaming end-to-end.",
  podcast: "Video podcast production from set to publish.",
  studiopodd: "Studio podcast format with full post-production.",
  animation: "Motion graphics and animation for ideas that need their own visual language.",
};

const services = primeServices.map((s) => ({
  slug: s.slug,
  title: primeShortTitlesEn[s.slug] ?? s.shortTitle,
  body: primeIntrosEn[s.slug] ?? s.intro,
}));

const process = [
  { n: "01", title: "Strategic planning", body: "We listen, understand the vision and frame the project." },
  { n: "02", title: "Brief & quote", body: "Script, lookboard, production plan and a clear estimate." },
  { n: "03", title: "Pre-production", body: "Scouting, casting, rigging, A/B-camera, screen tests, script and direction. Everything dialled in before we roll." },
  { n: "04", title: "Shoot", body: "Director, DPs, sound engineers and gaffers. The full crew on location with the right kit." },
  { n: "05", title: "Post & delivery", body: "Edit, grade, sound design, graphics and VFX. Revisions until it lands. Delivery in the formats you need." },
];

const primeCases = [featuredCase, ...caseItems.filter((c) => c.service === "Prime")].slice(0, 3);

export const Route = createFileRoute("/en_/prime")({
  head: () => ({
    meta: [
      { title: "Prime — Larger productions that demand more | Fenny" },
      { name: "description", content: "Prime is Fenny's premium track: commercials, documentary, TV, streaming, live and post-production. Tailor-made projects with director, script and full." },
      { property: "og:title", content: "Prime — Larger productions | Fenny" },
      { property: "og:description", content: "Tailor-made premium productions: commercials, documentary, streaming, live and post-production." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en/prime" },
      { property: "og:image", content: PRIME_OG_IMAGE },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Prime — Larger productions | Fenny" },
      { name: "twitter:description", content: "Tailor-made premium productions from Fenny." },
      { name: "twitter:image", content: PRIME_OG_IMAGE },
      { name: "language", content: "en" },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/en/prime" },
      { rel: "preconnect", href: "https://i.ytimg.com" },
      { rel: "preconnect", href: "https://www.youtube.com" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/prime", name: "Prime — Larger productions that demand more | Fenny", description: "Prime is Fenny's premium track: commercials, documentary, TV, streaming, live and post-production. Tailor-made projects with director, script and full."})) },
    ],

  }),
  component: EnPrimePage,
});

function EnPrimePage() {
  return (
    <div className="pt-28">
      {/* Hero */}
      <section className="relative isolate overflow-hidden pb-12 md:pb-20">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,color-mix(in_oklab,var(--butter)_8%,transparent)_0%,transparent_60%)]"
        />
        <GrainOverlay opacity={0.06} />
        <div className="container-wide">
          <Reveal>
            <LivePulseBadge tone="butter" className="mb-8">
              Prime · premium track
            </LivePulseBadge>
          </Reveal>
          <KineticHeading
            text="Larger productions that demand more."
            className="text-5xl font-semibold leading-[0.98] text-foreground md:text-8xl lg:text-[112px] max-w-6xl"
          />
          <Reveal delay={0.9}>
            <p className="mt-10 max-w-3xl text-pretty text-lg text-muted-foreground md:text-xl">
              When your brand needs something bigger than standard, Prime delivers. We bring experienced
              directors, creatives and technicians together to make video that doesn't just get seen —
              it stays in memory.
            </p>
          </Reveal>
          <Reveal delay={1.05}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Magnetic>
                <Button to="/en/contact" variant="mint" arrow trackId="contact_us" trackProps={{ location: "en_prime_hero" }}>
                  Request a Prime quote
                </Button>
              </Magnetic>
              <Button href="#disciplines" variant="outline">Prime disciplines</Button>
              <Button href="#process" variant="ghost">How Prime works</Button>
              <Button href="#cases" variant="ghost">Prime in action</Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Feature reel */}
      <Section className="py-10 md:py-14">
        <Reveal>
          <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-black p-2 md:p-3">
            <VideoEmbed id="9iiyNS4yNzA" title="Case — Maria at Garageportexperten" />
          </div>
        </Reveal>
      </Section>

      {/* Intro */}
      <Section className="py-16 md:py-24">
        <Reveal>
          <EyebrowHeading
            eyebrow="Vision into reality"
            title={<>Every strong idea deserves to move from thought into reality.</>}
            lede="We work closely with you to understand the vision behind the project — whether the goal is to build brand, drive sales or create something that moves people. Prime handles everything from creative brief to final delivery: scriptwriting, scouting, casting, shoot, edit, grade, sound design. Every phase with dedicated craft. The result: video that stands up to repeat viewing."
          />
        </Reveal>
      </Section>

      <FitCheck
        eyebrow="Is Prime right for you?"
        title="Prime isn't everything we do — but it's the big moments."
        fitTitle="Prime fits you if…"
        noFitTitle="Not right if…"
        fits={[
          "You're planning a campaign, brand film, documentary or live production",
          "The vision needs director, script and full pre-production",
          "You want creative and technical in one team",
        ]}
        noFits={[
          "You need a continuous monthly flow — see Pulse",
          "Budget under EUR 10k for a single production (Pilot may fit)",
        ]}
        tone="butter"
      />

      {/* Disciplines */}
      <Section id="disciplines" className="scroll-mt-28 bg-surface/40">
        <EyebrowHeading
          eyebrow="Prime disciplines"
          title="What we do under Prime"
          lede="Specialised disciplines — book one or several."
        />
        <RevealStagger className="mt-14 grid auto-rows-[minmax(220px,auto)] gap-5 md:grid-cols-3">
          {services.map((s, i) => {
            const featured = i === 0;
            return (
              <RevealItem key={s.title} className={featured ? "md:col-span-2 md:row-span-2" : ""}>
                <Link to="/prime/$slug" params={{ slug: s.slug }} className="group block h-full">
                  <TiltCard
                    glow="butter"
                    intensity={featured ? 4 : 6}
                    className={`flex h-full flex-col rounded-3xl border bg-surface p-7 transition-colors ${featured ? "border-butter/30 md:p-10" : "border-white/10 hover:border-butter/30"}`}
                  >
                    <div className="mb-4 text-xs font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                    <h3 className={featured ? "text-3xl font-semibold leading-tight md:text-5xl" : "text-xl font-semibold leading-tight"}>{s.title}</h3>
                    <p className={`mt-3 flex-1 leading-relaxed text-muted-foreground ${featured ? "text-base md:text-lg" : "text-sm"}`}>{s.body}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-butter transition-transform group-hover:translate-x-0.5">
                      Read more <ArrowRight className="h-4 w-4" />
                    </span>
                  </TiltCard>
                </Link>
              </RevealItem>
            );
          })}
        </RevealStagger>
      </Section>

      {/* Cases */}
      <Section id="cases" className="scroll-mt-28">
        <EyebrowHeading
          eyebrow="Prime in action"
          title="Three projects. Three ambition levels."
          lede="Open a case for the full breakdown — challenge, solution and outcome."
        />
        <RevealStagger className="mt-14 grid gap-5 md:grid-cols-3">
          {primeCases.map((c) => (
            <RevealItem key={c.slug}>
              <Link to="/case/$slug" params={{ slug: c.slug }} className="group block h-full">
                <TiltCard glow="butter" className="flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface transition-colors hover:border-butter/30">
                  <div className="relative aspect-video overflow-hidden">
                    <img src={`https://i.ytimg.com/vi/${c.id}/hqdefault.jpg`} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-butter">{c.client} · {c.year}</div>
                    <h3 className="mt-2 text-xl font-semibold leading-tight">{c.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{c.excerpt}</p>
                    {c.metric && (
                      <div className="mt-5 border-t border-white/10 pt-4">
                        <div className="text-2xl font-semibold text-butter">{c.metric.value}</div>
                        <div className="text-xs text-muted-foreground">{c.metric.label}</div>
                      </div>
                    )}
                  </div>
                </TiltCard>
              </Link>
            </RevealItem>
          ))}
        </RevealStagger>
        <div className="mt-10">
          <Button to="/en/work" variant="outline" arrow>See all cases</Button>
        </div>
      </Section>

      {/* Prime vs Pulse */}
      <Section id="vs-pulse" className="scroll-mt-28 bg-surface/40">
        <EyebrowHeading
          eyebrow="Prime vs Pulse"
          title="Many clients combine."
          lede="Pulse for the continuous flow & Prime for the big moments. Same team. Same project manager. Same invoice."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          <ComparisonCard
            tag="Pulse"
            title="Continuous content machine"
            items={["Fixed monthly subscription", "2–6 clips/month", "Optimised for social", "From EUR 1,395/month"]}
            cta={{ label: "Explore Pulse", to: "/en/pulse" }}
            tone="outline"
          />
          <ComparisonCard
            tag="Prime"
            title="Big moments"
            items={["Custom quote per project", "Commercials, documentary, TV & live", "Full pre-production & casting", "Budgets from EUR 14k"]}
            cta={{ label: "Request Prime quote", to: "/en/contact" }}
            tone="mint"
          />
        </div>
      </Section>

      {/* Process */}
      <Section id="process" className="scroll-mt-28">
        <EyebrowHeading eyebrow="How Prime works" title="Five phases. None skipped." />
        <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_2fr] lg:gap-16">
          <Reveal className="lg:sticky lg:top-28 lg:self-start">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-butter">Outcome</div>
              <div className="mt-3 text-5xl font-semibold leading-none md:text-6xl">
                <LiveCountUp value={97} suffix="%" />
              </div>
              <p className="mt-3 text-sm text-muted-foreground">of Prime productions delivered on the agreed premiere date.</p>
              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-butter">Avg lead time</div>
                <div className="mt-2 text-3xl font-semibold">
                  <LiveCountUp value={6} suffix=" w" />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">from brief to delivery.</p>
              </div>
            </div>
          </Reveal>
          <RevealStagger className="space-y-4">
            {process.map((p) => (
              <RevealItem key={p.n}>
                <div className="rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-butter/40">
                  <div className="flex items-baseline gap-5">
                    <span className="text-4xl font-semibold text-butter md:text-5xl">{p.n}</span>
                    <div>
                      <h3 className="text-xl font-semibold leading-tight md:text-2xl">{p.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-[15px]">{p.body}</p>
                    </div>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
        <div className="mt-14 flex flex-wrap gap-3">
          <Magnetic>
            <Button to="/en/contact" variant="mint" arrow trackId="contact_us" trackProps={{ location: "en_prime_process" }}>Contact us</Button>
          </Magnetic>
          <Button to="/en/work" variant="outline">Previous projects</Button>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pt-0">
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-gradient-to-br from-surface via-surface to-butter/[0.05] p-10 text-center md:p-14">
          <h2 className="text-balance text-3xl font-semibold md:text-4xl">Bring us the big idea.</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Tell us about the production you're planning. We'll come back with creative direction
            and a clear quote within a week.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Magnetic>
              <Button to="/en/contact" variant="mint" arrow>Request Prime quote</Button>
            </Magnetic>
            <Link to="/en" className="inline-flex items-center gap-2 text-sm text-foreground/70 underline-offset-4 hover:text-butter hover:underline">
              Back to overview →
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}

function ComparisonCard({
  tag,
  title,
  items,
  cta,
  tone,
}: {
  tag: string;
  title: string;
  items: string[];
  cta: { label: string; to: string };
  tone: "mint" | "outline";
}) {
  return (
    <Reveal>
      <article className={"relative flex h-full flex-col rounded-3xl border bg-surface p-8 transition-colors " + (tone === "mint" ? "border-mint/40" : "border-white/10 hover:border-white/25")}>
        <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">{tag}</div>
        <h3 className="text-3xl font-semibold leading-tight md:text-4xl">{title}</h3>
        <ul className="mt-6 space-y-3">
          {items.map((i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-foreground/90">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
              <span>{i}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8">
          <Button to={cta.to} variant={tone === "mint" ? "mint" : "outline"} arrow>{cta.label}</Button>
        </div>
      </article>
    </Reveal>
  );
}
